import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as quoteService from "../../services/quoteService";
import { QuotePayload, QuoteListItem } from "../../services/quoteService";

interface QuoteState {
  loading: boolean;
  error: string | null;
  success: boolean;
  currentQuoteId: string | null;
  quotes: QuoteListItem[];
  selectedQuote: QuoteListItem | null;
  listLoading: boolean;
  listError: string | null;
}

const initialState: QuoteState = {
  loading: false,
  error: null,
  success: false,
  currentQuoteId: null,
  quotes: [],
  selectedQuote: null,
  listLoading: false,
  listError: null,
};

export const createQuote = createAsyncThunk(
  "quotes/createQuote",
  async (data: QuotePayload) => {
    const response = await quoteService.createQuote(data);
    return response;
  }
);

export const markQuoteAsWon = createAsyncThunk(
  "quotes/markQuoteAsWon",
  async (id: string) => {
    const response = await quoteService.markQuoteAsWon(id);
    return response;
  }
);

export const convertLeadToClient = createAsyncThunk(
  "quotes/convertLeadToClient",
  async (leadId: string) => {
    const response = await quoteService.convertLeadToClient(leadId);
    return response;
  }
);

export const convertQuoteToWorkOrder = createAsyncThunk(
  "quotes/convertQuoteToWorkOrder",
  async (params: {
    quoteId: string;
    siteId: string;
    workOrderTitle: string;
    startDate: string;
    createSeparateWorkOrdersPerQuoteJob: boolean;
  }) => {
    const response = await quoteService.convertQuoteToWorkOrder(
      params.quoteId,
      params.siteId,
      params.workOrderTitle,
      params.startDate,
      params.createSeparateWorkOrdersPerQuoteJob
    );
    return response;
  }
);

export const revertAwardQuoteToWo = createAsyncThunk(
  "quotes/revertAwardQuoteToWo",
  async (quoteId: string) => {
    const response = await quoteService.revertAwardQuoteToWo(quoteId);
    return response;
  }
);

export const fetchQuotes = createAsyncThunk(
  "quotes/fetchQuotes",
  async (params: { pageNumber?: number; pageSize?: number }) => {
    const response = await quoteService.getAllQuotes(
      params.pageNumber || 1,
      params.pageSize || 200
    );
    return response;
  }
);

export const fetchQuoteById = createAsyncThunk(
  "quotes/fetchQuoteById",
  async (id: string) => {
    const response = await quoteService.getQuoteById(id);
    return response;
  }
);

export const updateQuote = createAsyncThunk(
  "quotes/updateQuote",
  async (params: { id: string; data: QuotePayload }) => {
    const response = await quoteService.updateQuote(params.id, params.data);
    return response;
  }
);

export const deleteQuote = createAsyncThunk(
  "quotes/deleteQuote",
  async (id: string) => {
    await quoteService.deleteQuote(id);
    return id;
  }
);


const quoteSlice = createSlice({
  name: "Quotes",
  initialState,
  reducers: {
    resetQuoteState(state) {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.currentQuoteId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Quote
      .addCase(createQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createQuote.fulfilled, (state, action: any) => {
        state.loading = false;
        state.success = true;
        state.currentQuoteId = action.payload?.id || action.payload?.data?.id;
      })
      .addCase(createQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create quote";
        console.error("Create Quote Failed:", action.error);
      })
      // Mark as Won
      .addCase(markQuoteAsWon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markQuoteAsWon.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(markQuoteAsWon.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to mark quote as won";
      })
      // Convert Lead
      .addCase(convertLeadToClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(convertLeadToClient.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(convertLeadToClient.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to convert lead";
      })
      // Convert Quote to WorkOrder
      .addCase(convertQuoteToWorkOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(convertQuoteToWorkOrder.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(convertQuoteToWorkOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to convert quote to work order";
      })
      // Revert Award Quote to WorkOrder
      .addCase(revertAwardQuoteToWo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revertAwardQuoteToWo.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(revertAwardQuoteToWo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to revert award quote to work order";
      })
      // Fetch Quotes List
      .addCase(fetchQuotes.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.listLoading = false;
        state.quotes = action.payload.items || [];
      })
      .addCase(fetchQuotes.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.error.message || "Failed to fetch quotes";
      })
      // Fetch Quote By ID
      .addCase(fetchQuoteById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuoteById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedQuote = action.payload;
      })
      .addCase(fetchQuoteById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch quote";
      })
      // Update Quote
      .addCase(updateQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.selectedQuote = action.payload;
        // Update quote in list if it exists
        const index = state.quotes.findIndex((q) => q.id === action.payload.id);
        if (index !== -1) {
          state.quotes[index] = action.payload;
        }
      })
      .addCase(updateQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update quote";
      })
      // Delete Quote
      .addCase(deleteQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.quotes = state.quotes.filter((q) => q.id !== action.payload);
      })
      .addCase(deleteQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete quote";
      });
  },
});

export const { resetQuoteState } = quoteSlice.actions;
export default quoteSlice.reducer;

// Selectors
export const selectQuoteLoading = (state: any) => state.Quotes.loading;
export const selectQuoteError = (state: any) => state.Quotes.error;
export const selectQuoteSuccess = (state: any) => state.Quotes.success;
export const selectCurrentQuoteId = (state: any) => state.Quotes.currentQuoteId;
export const selectQuoteList = (state: any) => state.Quotes.quotes;
export const selectQuoteListLoading = (state: any) => state.Quotes.listLoading;
export const selectQuoteListError = (state: any) => state.Quotes.listError;
export const selectSelectedQuote = (state: any) => state.Quotes.selectedQuote;
export const selectQuoteById = (state: any, id: string) => {
  const quotes = state.Quotes.quotes || [];
  return quotes.find((q: QuoteListItem) => q.id === id) || state.Quotes.selectedQuote;
};

